import { useMutation, useQuery } from "@apollo/client";

import {
  CREATE_FINANCE_INVOICE_MUTATION,
  GET_FINANCE_INVOICE_QUERY,
  LIST_FINANCE_INVOICES_QUERY,
  UPDATE_FINANCE_INVOICE_MUTATION,
} from "~/api";
import { ITEMS_PER_PAGE } from "~/constants";
import { defaultQuoteLineItemsOnCreateList } from "~/features/Quotes/detail/utils/lineItems";
import type {
  FinanceInvoice,
  FinanceInvoicesListResult,
} from "~/types/finance";
import { errorsHandler } from "~/transformers";
import { buildListFinanceInvoicesVariables } from "~/utils/finance/buildFinanceInvoicesQuery";

export const useListFinanceInvoices = ({
  activeFilters,
  searchQuery,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  sort = ["created_at:desc"],
  skipCondition = false,
}: {
  activeFilters?: Record<string, unknown>;
  searchQuery?: string;
  limit?: number;
  skip?: number;
  sort?: string[];
  skipCondition?: boolean;
} = {}) => {
  const variables = buildListFinanceInvoicesVariables({
    activeFilters,
    searchQuery,
    limit,
    skip,
    sort,
  });

  const { data, loading, error, refetch, fetchMore } = useQuery<{
    listFinanceInvoices: FinanceInvoicesListResult;
  }>(LIST_FINANCE_INVOICES_QUERY, {
    variables,
    skip: skipCondition,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const result = data?.listFinanceInvoices;

  const handleFetchMore = ({
    limit: moreLimit,
    skip: moreSkip,
  }: {
    limit: number;
    skip: number;
  }) => {
    const moreVariables = buildListFinanceInvoicesVariables({
      activeFilters,
      searchQuery,
      limit: moreLimit,
      skip: moreSkip,
      sort,
    });
    return fetchMore({
      variables: moreVariables,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.listFinanceInvoices) return prev;
        return {
          listFinanceInvoices: {
            ...fetchMoreResult.listFinanceInvoices,
            invoices: [
              ...(prev.listFinanceInvoices?.invoices ?? []),
              ...fetchMoreResult.listFinanceInvoices.invoices,
            ],
          },
        };
      },
    });
  };

  return {
    invoices: result?.invoices ?? [],
    totalCount: result?.totalCount ?? 0,
    loading,
    error,
    refetch,
    handleFetchMore,
  };
};

export const useCreateFinanceInvoice = () => {
  const [mutate, { loading }] = useMutation(CREATE_FINANCE_INVOICE_MUTATION);
  const [updateMutate] = useMutation(UPDATE_FINANCE_INVOICE_MUTATION);

  return {
    createFinanceInvoice: (input: {
      company: string;
      workOrder?: string;
      quote?: string;
    }) =>
      new Promise<FinanceInvoice>(async (resolve, reject) => {
        try {
          const { data } = await mutate({
            variables: { input },
            refetchQueries: [{ query: LIST_FINANCE_INVOICES_QUERY }],
          });
          const invoice = data?.createFinanceInvoice as
            | (FinanceInvoice & { lineItems?: Array<{ _id?: string }> })
            | undefined;
          // From quote/WO keep seeded rows; otherwise ensure one empty line item.
          if (invoice?._id && !invoice.lineItems?.length) {
            await updateMutate({
              variables: {
                input: {
                  _id: invoice._id,
                  lineItems: defaultQuoteLineItemsOnCreateList(),
                },
              },
              refetchQueries: [
                {
                  query: GET_FINANCE_INVOICE_QUERY,
                  variables: { id: invoice._id },
                },
                { query: LIST_FINANCE_INVOICES_QUERY },
              ],
            });
          }
          resolve(invoice);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};
